import { tw } from "@careinfo/tailwind";
import React from "react";
import Template from "../Datatable/loading";

interface TableProps {
  data: any;
}

const TableRow: React.FC<{ entry: any }> = ({ entry }) => {
  const {
    account,
    purchased_qty,
    purchased,
    sold_qty,
    sold,
    closing_qty,
    closing_value,
  } = entry;

  return (
    <div
      className={tw`grid grid-cols-5 p-2 border-t border-[#dcdcdc] bg-white`}
    >
      <div className={tw`flex justify-between`}>
        <span>{account.name}</span>
        <span>{account.unit?.name}</span>
      </div>

      <div className={tw`flex justify-around items-center`}>
        <span>{Number(account.opening_quantity).toFixed(3)}</span>
        <span>
          {Number(account.opening_balance).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      </div>

      <div className={tw`flex justify-around items-center`}>
        <span>{Number(purchased_qty).toFixed(3)}</span>
        <span>
          {Number(purchased).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      </div>

      <div className={tw`flex justify-around items-center`}>
        <span>{Number(sold_qty).toFixed(3)}</span>
        <span>
          {Number(sold).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      </div>

      <div className={tw`flex justify-around items-center`}>
        <span>{Number(closing_qty).toFixed(3)}</span>
        <span>
          {Math.abs(Number(closing_value)).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      </div>
    </div>
  );
};

const CategorySection: React.FC<{ categoryName: string; items: any[] }> = ({
  categoryName,
  items,
}) => {
  const totalOpeningQty = items.reduce(
    (sum, item) => sum + Number(item.account.opening_quantity || 0),
    0
  );
  const totalOpeningValue = items.reduce(
    (sum, item) => sum + Number(item.account.opening_balance || 0),
    0
  );

  const totalReceivedQty = items.reduce(
    (sum, item) => sum + Number(item.purchased_qty || 0),
    0
  );
  const totalReceivedValue = items.reduce(
    (sum, item) => sum + Number(item.purchased || 0),
    0
  );

  const totalDeliveredQty = items.reduce(
    (sum, item) => sum + Number(item.sold_qty || 0),
    0
  );
  const totalDeliveredValue = items.reduce(
    (sum, item) => sum + Number(item.sold || 0),
    0
  );

  const totalClosingQty = items.reduce(
    (sum, item) => sum + Number(item.closing_qty || 0),
    0
  );
  const totalClosingValue = items.reduce(
    (sum, item) => sum + Number(item.closing_value || 0),
    0
  );

  return (
    <>
      <div className={tw`text-primary font-bold p-2 border-t border-[#dcdcdc]`}>
        {categoryName}
      </div>

      {items.map((entry, idx) => (
        <TableRow key={idx} entry={entry} />
      ))}

      <div className={tw`grid grid-cols-5 bg-gray-200 font-semibold p-2`}>
        <span> TOTAL</span>
        <div className={tw`flex justify-around`}>
          <span>{totalOpeningQty.toFixed(3)}</span>
          <span>
            {Number(totalOpeningValue).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <div className={tw`flex justify-around`}>
          <span>{totalReceivedQty.toFixed(3)}</span>
          <span>
            {Number(totalReceivedValue).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <div className={tw`flex justify-around`}>
          <span>{totalDeliveredQty.toFixed(3)}</span>
          <span>
            {Number(totalDeliveredValue).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <div className={tw`flex justify-around`}>
          <span>{totalClosingQty.toFixed(3)}</span>
          <span>
            {Math.abs(Number(totalClosingValue)).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      </div>
    </>
  );
};

const Table: React.FC<TableProps> = ({ data }) => {

const allItems = Object.values(data).flat() as any[];
 const totals = {
    openingQty: allItems.reduce(
      (sum, item) => sum + Number(item.account.opening_quantity || 0),
      0
    ),
    openingValue: allItems.reduce(
      (sum, item) => sum + Number(item.account.opening_balance || 0),
      0
    ),
    receivedQty: allItems.reduce(
      (sum, item) => sum + Number(item.purchased_qty || 0),
      0
    ),
    receivedValue: allItems.reduce(
      (sum, item) => sum + Number(item.purchased || 0),
      0
    ),
    deliveredQty: allItems.reduce(
      (sum, item) => sum + Number(item.sold_qty || 0),
      0
    ),
    deliveredValue: allItems.reduce(
      (sum, item) => sum + Number(item.sold || 0),
      0
    ),
    closingQty: allItems.reduce(
      (sum, item) => sum + Number(item.closing_qty || 0),
      0
    ),
    closingValue: allItems.reduce(
      (sum, item) => sum + Number(item.closing_value || 0),
      0
    ),
  };
  return (
    <div className={tw`border border-[#dcdcdc] rounded-md overflow-hidden`}>
      <div className={tw`grid grid-cols-5 bg-primary text-white p-2`}>
        <div className={tw`flex justify-between`}>
          <span>Product Name</span>
          <span>Unit</span>
        </div>
        <div className={tw`text-center`}>
          <span>Opening</span>
          <div className={tw`flex justify-around`}>
            <span>Quantity</span>
            <span>Value</span>
          </div>
        </div>
        <div className={tw`text-center`}>
          <span>Received</span>
          <div className={tw`flex justify-around`}>
            <span>Quantity</span>
            <span>Value</span>
          </div>
        </div>
        <div className={tw`text-center`}>
          <span>Delivered</span>
          <div className={tw`flex justify-around`}>
            <span>Quantity</span>
            <span>Value</span>
          </div>
        </div>
        <div className={tw`text-center`}>
          <span>Closing</span>
          <div className={tw`flex justify-around`}>
            <span>Quantity</span>
            <span>Value</span>
          </div>
        </div>
      </div>

      {Object.entries(data).map(([categoryName, items]: [string, any[]]) => (
        <CategorySection
          key={categoryName}
          categoryName={categoryName}
          items={items}
        />
      ))}
      <div className={tw`grid grid-cols-5 bg-gray-300 font-bold p-2 mt-5`}>
        <span> GRAND TOTAL</span>
        <div className={tw`flex justify-around`}>
          <span>{totals.openingQty.toFixed(3)}</span>
          <span>
            {totals.openingValue.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <div className={tw`flex justify-around`}>
          <span>{totals.receivedQty.toFixed(3)}</span>
          <span>
            {totals.receivedValue.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <div className={tw`flex justify-around`}>
          <span>{totals.deliveredQty.toFixed(3)}</span>
          <span>
            {totals.deliveredValue.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <div className={tw`flex justify-around`}>
          <span>{totals.closingQty.toFixed(3)}</span>
          <span>
            {Math.abs(totals.closingValue).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      </div>
    


    </div>
  );
};

const StockValuationDetailTable: React.FC<{
  data: any;
  isLoading: boolean;
}> = ({ data, isLoading }) => {
  return (
    <div className={tw`space-y-4`}>
      {isLoading ? (
        <Template addNew={""} customSearch={""} noOfColumns={10} />
      ) : (
        <Table data={data} />
      )}
    </div>
  );
};

export default StockValuationDetailTable;
